import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Loader, Truck, ShieldCheck, Trash2 } from 'lucide-react';
import { useMyVehicles, useDeleteVehicle } from '@/hooks/useVehicles';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';

export default function MyVehiclesPage() {
  const { t } = useTranslation();
  const { data: vehicles, isLoading } = useMyVehicles();
  const removeMut = useDeleteVehicle();

  async function handleRemove(id: number) {
    if (!confirm(t('confirm_delete_vehicle', 'حذف هذه المركبة؟'))) return;
    try { await removeMut.mutateAsync(id); }
    catch (e: any) { alert(e?.message || 'فشل الحذف'); }
  }

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-text-primary">{t('my_vehicles', 'مركباتي')}</h1>
        <Link
          to="/vehicles/add"
          className="inline-flex items-center gap-1.5 bg-primary text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-primary-light"
        >
          <Plus size={16} /> {t('add_vehicle', 'إضافة مركبة')}
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12 text-text-tertiary">
          <Loader size={28} className="animate-spin" />
        </div>
      ) : !vehicles || vehicles.length === 0 ? (
        <div className="bg-surface border border-border rounded-xl p-10 text-center">
          <Truck size={40} className="mx-auto text-text-tertiary mb-3" />
          <p className="text-text-secondary mb-3">{t('no_vehicles', 'لم تُسجِّل مركبة بعد')}</p>
          <Link to="/vehicles/add" className="inline-block bg-primary text-white rounded-xl px-5 py-2.5 text-sm font-semibold">
            {t('add_first_vehicle', 'سجِّل أول مركبة')}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {vehicles.map((v) => (
            <div key={v.id} className="bg-surface border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-primary font-semibold">
                  <Truck size={18} />
                  <span>{v.brand} {v.model}</span>
                </div>
                {v.is_verified && (
                  <span className="text-success flex items-center gap-1 text-xs">
                    <ShieldCheck size={14} /> {t('verified', 'موثَّقة')}
                  </span>
                )}
              </div>
              <div className="text-sm text-text-secondary space-y-0.5">
                <div dir="ltr" className="font-mono">{v.plate_number}</div>
                {v.year && <div>{t('year', 'السنة')}: {v.year}</div>}
                {v.color && <div>{t('color', 'اللون')}: {v.color}</div>}
                {v.max_weight_kg && <div>{t('max_weight', 'الحمولة')}: {v.max_weight_kg} كغ</div>}
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  variant="ghost"
                  className="!text-error !border-error hover:!bg-error/10 !px-3 !py-1.5 !text-sm"
                  onClick={() => handleRemove(v.id)}
                >
                  <Trash2 size={14} /> {t('delete', 'حذف')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  );
}
